import type { FilterAttributeUrlKinds } from '@bastion/list-view';

/** URL + list-view attribute ids for episode filters (canonical chip ids). */
export const EPISODE_FILTER_IDS = [
  'source',
  'sop',
  'tag',
  'rating',
  'hostname',
  'hardware',
  'collector',
  // Legacy boolean review state. Kept in the canonical id list so old
  // shared URLs decode without errors, but no chip surfaces it anymore.
  // `buildQueryFilters` silently drops its values once URL state is
  // parsed — the new `reviewed_against` chip below is the replacement.
  'reviewed',
  // "Reviewed against [Review SOP]" — multi-select of Review SOP ids.
  // Server-derived: `useCloudEpisodes` resolves selected SOP ids to
  // hash-fresh approved episode id sets via `/episodes/reviewed-against`
  // and intersects with the rest of the query.
  'reviewed_against',
  // Inference-policy filters: `policy` chip targets `source_artifact_id`,
  // `checkpoint` chip targets `pinned_checkpoint_id`. Both are multi-select
  // and resolved server-side. The chip menu narrows checkpoint options to
  // the currently-selected policies for clarity but the URL encodes them
  // as independent multi-select params.
  'policy',
  'checkpoint',
  'interventions',
  // Actual subtask labels present in an episode's `episode_segments`.
  'subtask',
  // Coverage state for episode annotation completeness. Legacy URLs may still
  // use `subtasks`; query translation accepts both.
  'subtask_coverage',
  'subtasks',
  // Numeric range chip: full episode duration (frame_count / fps), in
  // seconds. Encodes as `?task_length_min=10&task_length_max=60` via the
  // shared `numeric-range` URL kind. Backed server-side by the
  // `episodes.duration_seconds` generated column.
  'task_length',
  'search',
  // Combined preset + absolute time range. Encodes as `?created=last_2h`
  // (preset) or `?created_from=<iso>&created_to=<iso>` (absolute). The URL
  // decoder also accepts legacy `?dates_from=...&dates_to=...` for backwards
  // compatibility with saved views authored before the rename.
  'created',
  // Explicit episode-id list. Encoded as repeated `?id=...&id=...` params via
  // the default `multi-select` URL kind. Intended for chat / Cursor / Slack
  // links that pin a specific set of episodes (~100 max in practice — URLs
  // larger than that should use a dataset instead).
  'id',
] as const;

export type EpisodeFilterId = (typeof EPISODE_FILTER_IDS)[number];

export const EPISODE_FILTER_URL_KINDS = {
  search: 'text-search',
  created: 'time-range',
  task_length: 'numeric-range',
} as const satisfies FilterAttributeUrlKinds;
